import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Table, TableHead, TableBody, TableRow, TableCell, TextField, IconButton,
    Paper,
    CircularProgress
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import moment from 'moment';
import { backendDateFormat, fieldDateFormat } from '../../../../constants';
import { globalDeleteService, globalGetService, globalPostService, globalPutService } from '../../../../utils_v2/globalApiServices';
import { checkApiStatus, getCurrencyFormat, toastFlashMessage } from '../../../../utils_v2';
import { EmptyCollection, PageLoader } from '../../../../shared_elements';
import NumericLabel from 'react-pretty-numbers';
import { regexConstants } from '../../../../constants/regEx';

export default function StipulationDialog({ open, onClose, contractId, insuranceId, props, id, variability, insuranceType }) {
    const [stipulations, setStipulations] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [originalStipulations, setOriginalStipulations] = useState([]);
    const [error, setError] = useState({});
    const [isLoading, setLoading] = useState(false);
    useEffect(() => {
        if (open) {
            getStipulationsList();
        }
    }, [open, contractId, insuranceId]);
    const getStipulationsList = () => {
        setLoading(true)
        const url = `contracts/${props?.params?.contract_slug}/insurances/${id}/insurance-variability/`;
        globalGetService(url)
            .then(response => {
                const data = response.data?.data || [];
                setOriginalStipulations(JSON.parse(JSON.stringify(data)));
                setStipulations(data);
                setLoading(false)
            })
    };
    const onFieldChange = (index, field, value) => {
        const updatedStipulations = [...stipulations];
        updatedStipulations[index][field] = value;
        if (field === 'stipulated_requirement' || field === 'variability_percentage') {
            for (let i = 1; i < updatedStipulations.length; i++) {
                const prevStipulated = parseFloat(updatedStipulations[i - 1].stipulated_requirement || 0);
                const variabilityPercent = parseFloat(updatedStipulations[i].variability_percentage || 0);
                const direction = variability === 'Decrease' ? -1 : variability === 'Increase' ? 1 : '';
                const factor = 1 + (direction * variabilityPercent / 100);
                let stipulatedRequirement = prevStipulated * factor;
                if (variability === 'Decrease' && stipulatedRequirement < 0) {
                    stipulatedRequirement = 0;
                }
                updatedStipulations[i].stipulated_requirement = isNaN(stipulatedRequirement) ? 0 : parseFloat(stipulatedRequirement.toFixed(2));
            }
        }
        setStipulations(updatedStipulations);
    };
    const handleSave = () => {
        let validationInputs = {};
        let hasError = false;

        stipulations.forEach((item, index) => {
            let startDateError = '';
            let endDateError = '';
            let variabilityError = '';
            let requirementError = '';

            if (!item.start_date) {
                startDateError = 'Enter Start Date';
            } else if (!moment(item.start_date, backendDateFormat, true).isValid()) {
                startDateError = 'Enter a valid Start Date';
            }

            if (!item.end_date) {
                endDateError = 'Enter End Date';
            } else if (!moment(item.end_date, backendDateFormat, true).isValid()) {
                endDateError = 'Enter a valid End Date';
            } else if (moment(item.end_date).isBefore(moment(item.start_date))) {
                endDateError = 'End Date must be after or equal to Start Date';
            }

            if (index === 0) {
                if (!item.stipulated_requirement || isNaN(item.stipulated_requirement)) {
                    requirementError = 'Enter Stipulated Requirement';
                }
            } else {
                if (!item.variability_percentage && item.variability_percentage !== 0) {
                    variabilityError = 'Enter Variability percentage';
                }
                if (item?.variability_percentage > 100) {
                    variabilityError = "Variability percentage can't greater than 100%";
                }
                if (item?.variability_percentage < 0) {
                    variabilityError = "Variability percentage can't be in negative.";
                }
            }
            if (startDateError || endDateError || variabilityError || requirementError) {
                hasError = true;
                validationInputs[index] = {
                    ...(validationInputs[index] || {}),
                    start_date: startDateError,
                    end_date: endDateError,
                    variability_percentage: variabilityError,
                    stipulated_requirement: requirementError
                };
            }
        });

        setError(validationInputs);
        if (!hasError) {
            const payload = stipulations.map((item, index) => ({
                id: item.id ?? null,
                start_date: item.start_date || null,
                end_date: item.end_date || null,
                stipulated_requirement: item.stipulated_requirement === 0 ? 0 : item.stipulated_requirement || null,
                variability_percentage: index === 0 ? 0 : item.variability_percentage || null
            }));
            setLoading(true)
            globalPostService(`contracts/${props?.params?.contract_slug}/insurances/${id}/insurance-variability/`, payload)
                .then(response => {
                    if (checkApiStatus(response)) {
                        setEditMode(false);
                        setLoading(false)
                        getStipulationsList();
                    }
                });
        }
    };

    const handleAddRow = () => {
        setStipulations(prev => [...prev, { start_date: '', end_date: '', stipulated_requirement: '', variability_percentage: '' }
        ]);
    };

    const handleDeleteRow = (index) => {
        const stipulationData = stipulations[index];
        if (stipulationData.id) {
            globalDeleteService(`contracts/${props?.params?.contract_slug}/insurances/${id}/insurance-variability/${stipulationData.id}/`)
                .then(response => {
                    if (checkApiStatus(response)) {
                        setStipulations(prev => prev.filter((_, i) => i !== index));
                        toastFlashMessage(response?.data?.message, 'success');
                    } else {
                        toastFlashMessage(response?.data?.message || 'Failed to delete stipulation', 'error');
                    }
                });
        } else {
            setStipulations(prev => prev.filter((_, i) => i !== index));
        }
    };

    const handleCancel = () => {
        if (editMode) {
            setStipulations(JSON.parse(JSON.stringify(originalStipulations)));
            getStipulationsList()
            setEditMode(false);
        } else {
            onClose();
        }
    };

    return (
        <>
            <Dialog open={open} onClose={() => { onClose(); setEditMode(false); }} maxWidth="md" fullWidth>
                <DialogTitle>{editMode ? 'Add/Edit ' : 'View '} Variability Schedule </DialogTitle>
                <DialogContent dividers>
                    <Paper style={{ padding: '4px' }}>
                        <h6>{insuranceType} Variability Schedule</h6>
                        <span style={{ display: 'flex' }}>Variability :<p style={{ fontFamily: 'Conv_IBMPlexSans-medium' }}> {variability}</p></span>
                    </Paper>
                    <Paper>
                        {variability === null || variability === undefined ? <span ><p style={{ background: 'yellow', width: 'fit-content' }}>Please Add Variability in the Particular Insurance tab to add stipulation.</p></span> : null}
                        {!editMode ? <div style={{ padding: '4px', float: 'right' }}><Button disabled={variability === null || variability === undefined} onClick={() => setEditMode(true)} color="primary" variant="outlined">Add/Edit Insurance Stipulations</Button></div> : null}
                        <Table className='mui-table-format lease-management-modal'>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Start Date</TableCell>
                                    <TableCell>End Date</TableCell>
                                    <TableCell>Stipulated Requirement(USD)</TableCell>
                                    <TableCell>Variability (%)</TableCell>
                                    {editMode && <TableCell />}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {stipulations.map((stipulationData, index) => (
                                    <TableRow key={index}>
                                        <TableCell>
                                            {editMode ? (
                                                <MuiPickersUtilsProvider utils={MomentUtils}>
                                                    <KeyboardDatePicker
                                                        format={fieldDateFormat}
                                                        value={stipulationData?.start_date || null}
                                                        onChange={(date) => { onFieldChange(index, 'start_date', date ? moment(date).format(backendDateFormat) : null); setError(prev => ({ ...prev, [index]: { ...prev[index], start_date: '' } })) }}
                                                        fullWidth
                                                        inputVariant="outlined"
                                                        margin="none"
                                                        error={!!(error[index]?.start_date)}
                                                        helperText={error[index]?.start_date}
                                                        onFocus={() => setError(prev => ({ ...prev, [index]: { ...prev[index], start_date: '' } }))}
                                                    />
                                                </MuiPickersUtilsProvider>
                                            ) : (
                                                stipulationData?.start_date ? moment(stipulationData?.start_date).format(fieldDateFormat) : '--'
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {editMode ? (
                                                <MuiPickersUtilsProvider utils={MomentUtils}>
                                                    <KeyboardDatePicker
                                                        format={fieldDateFormat}
                                                        value={stipulationData?.end_date ? stipulationData.end_date : null}
                                                        onChange={(date) => { onFieldChange(index, 'end_date', date ? moment(date).format(backendDateFormat) : null); setError(prev => ({ ...prev, [index]: { ...prev[index], end_date: '' } })) }}
                                                        error={!!(error[index]?.end_date)}
                                                        helperText={error[index]?.end_date}
                                                        fullWidth
                                                        inputVariant="outlined"
                                                        margin="none"
                                                        onFocus={() => setError(prev => ({ ...prev, [index]: { ...prev[index], end_date: '' } }))}
                                                    />
                                                </MuiPickersUtilsProvider>
                                            ) : (
                                                stipulationData.end_date ? moment(stipulationData.end_date).format(fieldDateFormat) : '--'
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {editMode ? (
                                                <TextField
                                                    id="stipulated_requirement"
                                                    fullWidth
                                                    margin="normal"
                                                    value={stipulationData?.stipulated_requirement}
                                                    variant='outlined'
                                                    InputLabelProps={{ shrink: true }}
                                                    inputProps={{ readOnly: index !== 0, style: { cursor: index === 0 ? 'text' : 'not-allowed' }}}                                                    
                                                    onChange={(e) => regexConstants.numberWithDot.test(e.target.value) ? onFieldChange(index, 'stipulated_requirement', e.target.value) : e.preventDefault()}
                                                    error={!!(error[index]?.stipulated_requirement)}
                                                    helperText={error[index]?.stipulated_requirement}
                                                    onFocus={() => setError(prev => ({ ...prev, [index]: { ...prev[index], stipulated_requirement: '' } }))}
                                                />

                                            ) : (
                                                `US$ ${stipulationData.stipulated_requirement === 0 ? 0 : stipulationData.stipulated_requirement || '--'}`
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {editMode ? (
                                                <TextField
                                                    type='number'
                                                    fullWidth
                                                    variant="outlined"
                                                    inputProps={{ readOnly: index === 0, style: { cursor: index !== 0 ? 'text' : 'not-allowed' }}}                                                    
                                                    value={index === 0 ? 0 : stipulationData?.variability_percentage || ''}
                                                    onChange={(e) => onFieldChange(index, 'variability_percentage', e.target.value)}
                                                    error={!!(error[index]?.variability_percentage)}
                                                    helperText={error[index]?.variability_percentage}
                                                    onFocus={() => setError(prev => ({ ...prev, [index]: { ...prev[index], variability_percentage: '' } }))}
                                                />
                                            ) : (
                                                stipulationData.variability_percentage === 0 ? 0 : stipulationData.variability_percentage || '--'
                                            )}
                                        </TableCell>
                                        {editMode && index !== 0 && (
                                            <TableCell>
                                                <IconButton onClick={() => handleDeleteRow(index)} size="small">
                                                    <DeleteIcon color='secondary' fontSize='small' />
                                                </IconButton>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        {!stipulations.length && !editMode ? <EmptyCollection description='No Records Found' /> : null}
                        {editMode && (<Button endIcon={<AddIcon />} disabled={variability === 'Fixed' && stipulations.length >= 1} onClick={handleAddRow} color="primary" variant='outlined' style={{ margin: '5px' }}>Add</Button>)}
                    </Paper>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { handleCancel() }} size='small' variant='outlined' color="primary">Cancel</Button>
                    {editMode ? <Button onClick={handleSave} size='small' color="primary" variant="contained">{isLoading ? <CircularProgress color="#ffffff" size={24} /> : `Save`}</Button> : null}
                </DialogActions>
            </Dialog>
            {isLoading ? <PageLoader /> : null}
        </>
    );
}
